// Wire contract for the Maven run delivery (CR-20260910-001, contract M1 v0.3 + rulings L13/L14/L28/L33/L39).
//
// These mirror the FROZEN backend DTOs at
//   ets.platform: ETS.ShellService.Shell.Protocol/Dto/Maven/MavenResultReadyDto.cs
//                 ETS.ShellService.Shell.Protocol/Dto/Maven/MavenRunStageDto.cs
// read at 7135fc4a. Field names are camelCase: the inbound ESS push arrives camelCase on the
// frontend. Do NOT add class-transformer decorators — the Maven pushes travel the same path as the
// OPG CoPilot's, which is decorator-free for the same reason.
//
// DATE RULE: every date crosses as a calendar STRING 'yyyy-MM-dd', never a JS Date. A Date
// serialised at local midnight shifts a calendar day when parsed as an instant in another
// timezone, and every date here is a chain key.
//
// Two nested shapes are declared LOCALLY rather than imported: the short-strike pair (the APG
// ORION's, lane S2) and the close solution (the Hedge Matrix Close's, lane S3). Neither exists on
// the frontend yet. They are declared here as Maven's own view of the frozen wire, which is what
// this file is for; if those lanes later publish frontend types, these stay valid because both
// mirror the same frozen DTOs.

import {CashFlowAdjustment} from '../../adjustment-pricing-grid/model/CashFlowAdjustment';
import {HedgeStateTransaction} from '../../hedging-grid/data-model/hedge-state-transaction';
import {OpgCoPilotTradeDto} from '../../copilot-shared/opg-copilot-operations';
import {MavenExistingHedges} from '../maven-domain';

// The two ESS message types the panel subscribes to (= the backend class names; registered in the
// three routing sites by the foundation pass, which this lane does not touch).
export const MAVEN_RESULT_READY_MESSAGE_TYPE = 'MavenResultReadyDto';
export const MAVEN_RUN_STAGE_MESSAGE_TYPE = 'MavenRunStageDto';

// The closed set of backend-emittable run outcomes (backend MavenRunOutcomes).
export const MAVEN_OUTCOME_SUCCEEDED = 'succeeded';
export const MAVEN_OUTCOME_EMPTY = 'empty';
export const MAVEN_OUTCOME_SUPERSEDED = 'superseded';
export const MAVEN_OUTCOME_TERMINAL_FAILED = 'terminalFailed';
export const MAVEN_OUTCOME_PRE_SEND_BLOCKED = 'preSendBlocked';

// The card's identity WITHIN its date (backend MavenHedgePairPlanner.Label, CR-20260921-003):
// "c1p2" = the calls hedge the ORION ranked first with the puts hedge it ranked second; "c1" /
// "p2" when one side only was requested. Unique per date by construction — the applied-marks
// key is date + variant. Not prose; never rendered. Before CR-20260921-003 it was one of
// "primary" | "secondHedge" | "mvp2" under the two-package rule, which that CR replaced.
export const MAVEN_VARIANT_FIRST_PAIR = 'c1p1';

// Calendar verdicts on MavenNearbyReport.tier. "unknown" is a day the calendar cannot vouch for and
// is NEVER presented as clear (guarantee G4).
export const MAVEN_TIER_1 = 'tier1';
export const MAVEN_TIER_2 = 'tier2';
export const MAVEN_TIER_CLEAR = 'clear';
export const MAVEN_TIER_UNKNOWN = 'unknown';

/**
 * The ONE delivery per run. Exactly one arrives, when the run completes or fails; stage events are
 * not deliveries (guarantee G6).
 */
export interface MavenResultReady {
    topic: string;                       // = TerminalId, the ESS routing key
    runId: string;                       // echoes the command's runId
    outcome: string;                     // MAVEN_OUTCOME_*
    failureDetail: string | null;        // failures, refusals, AND the ONE sentence an 'empty' run shows
    defaultQty: number;                  // the quantity every normalized figure refers to

    /**
     * "single" | "double" — the portfolio fact BOTH order pop-ups need (ruling L39).
     *
     * Delivered, never inferred from the adjustments on a card. Both failure modes are silent: a
     * wrong isDoubleMode makes the hedge pop-up filter legs away inside itself, and a wrong
     * strategyType writes the after-state into the wrong slot. Neither raises anything.
     */
    strategyType: string;

    /**
     * The Existing Hedges choice the run was SENT with — 'close' | 'ignore'. NOT on the wire: the
     * panel stamps it from the run at settle. It decides the card's breakdown shape (owner,
     * vrequest 2026-09-13, third pass): 'close' shows Adjustment / New hedges / Closing hedges;
     * 'ignore' shows Adjustment / Hedges. The Setup CHOICE triggers the third line, not the
     * close-out figure — a close that prices at zero still shows its line.
     */
    existingHedges: MavenExistingHedges;

    /**
     * Ranked, best first: EVERY package the run produced, never a capped set. The "show top N"
     * setting is this panel's DISPLAY limit, applied at render (see maven-card-display.ts).
     * Two or more, or empty. Empty unless the outcome is 'succeeded' (guarantee G1).
     */
    cards: MavenCard[] | null;

    /**
     * CR-20260914-001 — THE SCOPED EXCEPTION TO R-C (owner ruling 2026-09-14). One line per (date,
     * side) on which the hedge could not be built to the EXPECTED RANGE, because the day's band
     * ended at or short of the hedge start. Worded on the backend (ProtoMember(8)), in date order.
     * Present on 'succeeded' beside the cards AND on 'empty' beside the one sentence. Null or empty
     * = nothing to say. The ONE cause of a dropped day this column ever names.
     */
    expectedRangeNotes: string[] | null;
}

export interface MavenCard {
    rank: number;                        // 1..N, the RUN's rank — kept through a manual re-sort
    date: string;                        // the package's expiration, 'yyyy-MM-dd'
    dte: number;                         // CALENDAR days out, floored, from Eastern-time today or the QG cursor
    variant: string;                     // MAVEN_VARIANT_*
    adjustment: MavenAdjustment;
    hedges: MavenHedges;
    prices: MavenPrices;                 // normalized to defaultQty, signed
    pricesRaw: MavenPrices;              // the same five figures raw (x defaultQty x 100)
    notes: string[] | null;              // supplier codes from a CLOSED list; Maven adds none
    coverageDelivered: MavenCoverageDelivered | null;
    nearbyReports: MavenNearbyReport[] | null;
    tickers: string[] | null;            // every leg's ticker, for the live subscription
    score: number | null;                // ALWAYS null in iteration 1 — the card renders an explicit non-value
}

export interface MavenAdjustment {
    /** The adjustment pop-up's input, VERBATIM. Maven maps nothing. */
    adjustments: CashFlowAdjustment[] | null;
    adjustmentTypes: string[] | null;
    shortStrikes: MavenShortStrikes | null;
    atm: number;
    reason: string | null;

    /**
     * The option strategy codes this panel subscribes to in order to re-price the adjustment live.
     *
     * AN ARRAY (ruling L13) — a COUPLE's cell holds TWO strategies and therefore two codes. One
     * price per code, each taken as delivered, summed into the cell; the cell is unpriced if ANY
     * code is. Subscribing to one code of a couple would drift the card away from the price it was
     * delivered at, on every tick.
     */
    strategyCodes: string[] | null;
}

export interface MavenShortStrikes {
    call: number | null;
    put: number | null;
}

export interface MavenHedges {
    calls: OpgCoPilotTradeDto | null;    // the ORION (OPG)'s first delivered trade, VERBATIM; null = side not requested
    puts: OpgCoPilotTradeDto | null;
    close: MavenCloseSolution | null;    // null on Ignore — nothing ordered, nothing in the package
    /**
     * The hedge pop-up's input: close plus open, built on the backend. Maven maps nothing.
     *
     * Also the ONLY thing the browser can price a hedge from: the legs reachable here carry ticker,
     * quantity, side and asset, where `tickers` above is a flat subscription list carrying none of
     * them (ruling L61).
     *
     * ⚠ The frontend `HedgeOrderGroup` inside this structure is NOT the whole wire shape (ruling
     * L64): the backend class carries `OptionStrategyCode` at ProtoMember(5) and the frontend
     * interface does not declare it, so it ARRIVES and is invisible to TypeScript — undeclared, not
     * missing. Nothing needs it today, because a hedge prices by its legs and not by a code. Named
     * here because two types share one name with different fields, and the next person reasoning
     * across that boundary will hit it.
     */
    transaction: HedgeStateTransaction[] | null;
}

/** Mirrors HmCloseSolutionDto (lane S3's wire shape; no frontend type exists yet). */
export interface MavenCloseSolution {
    targetHedgeId: string;
    title: string | null;
    legs: MavenCloseLeg[] | null;
    prices: MavenClosePrices | null;
}

/**
 * Mirrors HmCloseLegDto. Unlike the OPEN hedge legs, a close leg carries its own ticker, quantity
 * and side — so the close-out figure IS re-priceable in the browser.
 */
export interface MavenCloseLeg {
    quantity: number;                    // as held
    ticker: string;
    displayName: string | null;
    action: string | null;               // "Sell To Close" | "Buy To Close"
    side: string | null;
    effect: string | null;
    groupId: string | null;
    asset: string | null;                // an ES leg prices at the norm factor
    optionType: string | null;           // "Call" | "Put"
    strike: number | null;
}

export interface MavenClosePrices {
    net: number | null;                  // combo net over the actual leg quantities
    orderPrice: number | null;           // net / HCF
    ledgerPrice: number | null;          // net / default quantity
}

/** Every figure is nullable: an unpriced part is NEVER carried as zero (guarantee G4). */
export interface MavenPrices {
    adjustment: number | null;
    hedges: number | null;
    closeOut: number | null;
    total: number | null;
    perDay: number | null;               // the ranking key — shown in the Summary, NOT on the card (D2)
}

/**
 * The structure the hedge engine ACTUALLY delivered per side, NAMED IN FULL — "Condor - Low",
 * "Unbalanced Condor - Aggressive", "BOS-Mid - Low", "Condor - $500 risk". Not the coverage the
 * trader asked for: the engine reports no coverage kind on a single-family run.
 *
 * CR-20260917-001 (owner screencast 2026-09-17) widened this from the bare mold word. "Condor"
 * alone is true of a balanced condor AND of one whose brake pair trades at double quantity, and a
 * trader who accepts the first but not the second could only tell them apart by opening the hedge
 * pop-up and reading the leg quantities. The risk level rides the same string as a suffix.
 *
 * ⚠ PROSE, NOT A MOLD. It is `OpgCoPilotTradeDto.moldDescription` and is NOT a `StrategyTemplate`
 * member — do not resolve a strategy template from it. The frozen union literal is still
 * `hedges.calls.mold` / `hedges.puts.mold`, unchanged by that CR.
 *
 * ⚠ DELIVERED, NEVER DERIVED. Maven does not inspect the supplier's legs to decide what the
 * structure is or whether it is balanced — the ORION declares both.
 */
export interface MavenCoverageDelivered {
    calls: string | null;
    puts: string | null;
}

/**
 * A calendar verdict for one date in the window around a card's date.
 *
 * THE CARD'S OWN DATE, AND ONLY THAT (CR-20260921-001, owner ruling 2026-09-21 — the neighbour
 * window of ruling L14 / contract M1 item 11 is gone; the field name stayed so the wire did not
 * move). Under a Maven order the ORION keeps landmine days, so a Tier 1 entry arrives here too.
 * Name the report; never explain why the day survived.
 */
export interface MavenNearbyReport {
    date: string;                        // 'yyyy-MM-dd'
    tier: string;                        // MAVEN_TIER_*
    events: string[] | null;
}

/** A stage event. SEVERAL arrive per run, and none of them is a delivery. */
export interface MavenRunStage {
    topic: string;
    runId: string;
    stage: string;                       // MAVEN_STAGE_* — an IDENTIFIER, never rendered raw (ruling L33)
    /**
     * Optional free text. It carries NO count of days, of cards, or of anything else (ruling L28):
     * a figure here is something the trader can subtract the visible cards from, which is the
     * reconciliation R-C exists to prevent.
     */
    detail: string | null;
}
